"use client";

import { v4 as uuid } from "uuid";

import { maxComboOptionInstruments } from "#/config/combo";
import { defaultUnderlying } from "#/config/markets";
import { useMarket } from "#/store/app/portfolio/market";
import { useComboActiveInstrumentId } from "#/store/combo/comboActiveInstrumentId";
import type { ComboInstrument } from "#/store/combo/comboInstruments";
import { useComboInstruments } from "#/store/combo/comboInstruments";
import { useMaturity } from "#/store/options/page/maturity";
import { type TradeDirection } from "#/types/trade";
import { isBuy } from "#/utils/marketActionConverter";
import { nextMaturities } from "#/utils/maturities";

import { sortLegsFn, updateLegRatios } from "./useComboInstruments.helpers";

const defaultInstrument: ComboInstrument = {
  id: "",
  type: "options",
  side: "buy",
  underlying: defaultUnderlying,
  optionType: "call",
  maturity: nextMaturities()[0],
  strike: 0,
  legPrice: NaN,
  legRatio: 1,
  size: "",
};

export const useComboInstrumentsController = () => {
  const { market } = useMarket();
  const { maturity } = useMaturity();
  const {
    comboInstruments,
    comboPerpInstruments,
    comboOptionInstruments,
    setComboInstruments,
  } = useComboInstruments();
  const { comboActiveInstrumentId } = useComboActiveInstrumentId();

  const buildInstrument = (
    overrides?: Partial<ComboInstrument>,
  ): ComboInstrument => {
    return {
      ...defaultInstrument,
      id: uuid(),
      maturity,
      underlying: market.underlying,
      ...overrides,
    };
  };

  const handleAddLeg = (instrument?: ComboInstrument) => {
    if (comboOptionInstruments.length >= maxComboOptionInstruments) return;

    // Sort perps to the end
    setComboInstruments((previous) => {
      const updated = [...previous, buildInstrument(instrument)];
      return updateLegRatios(updated).sort(sortLegsFn);
    });
  };

  const handleAddLegs = (instruments: ComboInstrument[], replaceAll = true) => {
    if (
      !replaceAll &&
      instruments.length + comboOptionInstruments.length >
        maxComboOptionInstruments
    )
      return;

    setComboInstruments((previous) =>
      [...(replaceAll ? [] : previous), ...instruments].sort(sortLegsFn),
    );
  };

  const handleEditLeg = (instrument: ComboInstrument) => {
    setComboInstruments((previous) => {
      const updated = previous.map((i) =>
        i.id === comboActiveInstrumentId ? { ...i, ...instrument } : i,
      );

      return updateLegRatios(updated);
    });
  };

  const handleAddPerp = () => {
    if (comboPerpInstruments.length) return;

    setComboInstruments((previous) => [
      ...previous,
      buildInstrument({ type: "perps" }),
    ]);
  };

  const handleDelete = (id: string) => {
    setComboInstruments((previous) =>
      previous.filter((item) => item.id !== id),
    );
  };

  const handleDeleteAll = () => {
    setComboInstruments([]);
  };

  const handleChange = (
    index: number,
    field: keyof ComboInstrument,
    value: string | number | boolean,
  ) => {
    setComboInstruments((previous) => {
      const updated = [...previous];
      updated[index] = { ...updated[index], [field]: value };

      return updateLegRatios(updated);
    });
  };

  const handleClearSizes = () => {
    setComboInstruments((previous) =>
      previous.map((leg) => ({
        ...leg,
        size: "",
      })),
    );
  };

  const handleFlipDirections = () => {
    setComboInstruments((previous) =>
      previous.map((leg) => ({
        ...leg,
        side: (isBuy(leg.side) ? "sell" : "buy") as TradeDirection,
      })),
    );
  };

  return {
    comboInstruments,
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLeg,
    handleDelete,
    handleChange,
    handleAddLegs,
    handleEditLeg,
    handleAddPerp,
    handleDeleteAll,
    buildInstrument,
    handleClearSizes,
    handleFlipDirections,
  };
};
